/**
 * 信用卡分期（B 域）契约 —— **批 `104` · 16 条必测 AC 的唯一形状来源**
 *
 * 依据：`DR-060`（口径定稿）· B 域 PRD 增补 **§6.4**（契约字段）/ §7（边界 E1–E10）/ §8（`AC-B2.11`–`26`）·
 * 规格 §7 / §8 / §12 / §18（2026-10-24 之三十九）· `DR-036`（金额一律整数分）。
 *
 * ## 🔴 四条形状纪律
 *
 * | # | 纪律 | 为什么 |
 * |:--:|---|---|
 * | 1 | **金额一律整数分**（`Cents`），**端侧零加算** | 规格 §22.1；`remainingPrincipal` / `unassignedAmount` / `totalRemainingPrincipal` 全部服务端算好下发 |
 * | 2 | 🔴 **`srcKind` 显式、不得留空兜底** | `DR-060` D5：`txn`（关联消费）/ `bill_amount`（账单金额）是两种**不同形态**，含糊 = 溯源失效（`AC-B2.21`） |
 * | 3 | 🔴 **可选键按需剔键** | 已结清计划的 `nextDue*` 三键**不出**（`null` 与"没有下一期"不可混谈） |
 * | 4 | **权限 = member 可**（规格 §16） | 日常业务写操作开放给 member，全控制器无 `@RequireOwner` |
 */

import { z } from 'zod';
import { DayString } from './pagination.js';

// ─────────────────────────── 输入 ───────────────────────────

const CentsInput = z
  .number({ message: '金额必须是数字（整数「分」）' })
  .int('金额必须是整数「分」（规格 §22.1）')
  .nonnegative('金额不得为负');

/**
 * `POST /api/installments` —— 建分期。
 *
 * 🔴 `srcKind` 语义：请求**显式**传 `srcKind='txn'` 时 `sources` 必须至少 1 条（`E2` /
 * `AC-B2.21`）；`sources` 非空 ⇒ `srcKind` 必须为 `'txn'`；省略 `srcKind` 时按
 * `sources` 推导（有 ⇒ `txn`，无 ⇒ `bill_amount`）—— 但落库的 `src_kind` **永远显式**，
 * 不得留空兜底（`DR-060` D5）。
 */
export const CreateInstallmentRequestSchema = z
  .object({
    /** 信用卡账户 id（必须是本账本 `type='credit'` 且**已配账期**的账户 —— `E9`） */
    accountId: z.string().trim().min(1, '账户 id 不得为空'),
    /** 本金总额（分，> 0；`E1`：不得超过 `|卡余额|`） */
    principalTotal: z.number({ message: '本金必须是数字（整数「分」）' }).int().positive('本金必须大于 0'),
    /** 期数（`E4`：2–60） */
    periods: z
      .number({ message: '期数必须是数字' })
      .int()
      .min(2, '期数至少 2 期')
      .max(60, '期数最多 60 期'),
    /** 手续费录入方式：`total` 总手续费（自动平摊）/ `per_period` 每期手续费 */
    feeMode: z.enum(['total', 'per_period']),
    /** 与 `feeMode` 配套的手续费金额（分，可为 0 = 免息分期，`E5`） */
    feeValue: CentsInput,
    /** 首期账单日 `YYYY-MM-DD`（🔴 必须是该卡的有效账单日，`E9` 家族校验） */
    startBillDay: DayString,
    /** 展示名；缺省「信用卡分期」（端侧缺省文案） */
    name: z.string().trim().max(50, '名称最长 50 字').optional(),
    /**
     * 🆕 来源形态（`DR-060` D5）：显式传 `'txn'` ⇒ `sources` 必须至少 1 条；
     * 省略时由 `sources` 推导。
     */
    srcKind: z.enum(['txn', 'bill_amount']).optional(),
    /**
     * 关联的来源消费（可多笔、可只覆盖单笔的部分金额；Σ ≤ 本金，`E3`）。
     * 🔴 同一笔交易只能出现一次（载体唯一键 `(plan_id, transaction_id)`）。
     */
    sources: z
      .array(
        z.object({
          transactionId: z.string().trim().min(1, '来源交易 id 不得为空'),
          /** 该笔被分期的金额（分，> 0；允许 < 该笔交易总额 = 部分分期） */
          amount: z.number().int().positive('关联金额必须大于 0'),
        }),
      )
      .max(50, '关联消费最多 50 笔')
      .optional(),
  })
  .strict();

export type CreateInstallmentRequest = z.infer<typeof CreateInstallmentRequestSchema>;

/**
 * `POST /api/installments/:id/pay` —— 还一期。
 *
 * `periodIndex` 缺省 = **最早** pending 期（"接着还下一期"）。
 */
export const PayInstallmentRequestSchema = z
  .object({
    /** 付款账户（储蓄卡 / 电子钱包；不能是分期所在卡自身） */
    accountId: z.string().trim().min(1, '付款账户 id 不得为空'),
    periodIndex: z.number().int().min(0).optional(),
  })
  .strict();

export type PayInstallmentRequest = z.infer<typeof PayInstallmentRequestSchema>;

// ─────────────────────────── 视图 ───────────────────────────

/** 来源消费（分期详情的溯源列表 · `AC-B2.23`） */
export interface InstallmentSourceView {
  transactionId: string;
  /** 来源消费日期 `YYYY-MM-DD` */
  date: string;
  /** 商户 / 备注（`transactions.note`） */
  merchant: string | null;
  /** 该笔被分期的金额（分）—— 可小于交易总额 */
  amount: number;
  /** 来源消费的分类（原分类，消费时已计支出） */
  categoryId: string | null;
}

/** 期次（分期详情展开） */
export interface InstallmentPeriodView {
  periodIndex: number;
  /** 到期日 `YYYY-MM-DD` */
  dueDate: string;
  /** 本期本金（分） */
  principal: number;
  /** 本期手续费（分）—— `loan_schedules.interest` 列的分期语义 */
  fee: number;
  status: 'pending' | 'paid';
  /** 还本金的转账交易 id（未还 ⇒ `null`） */
  paidTransactionId: string | null;
}

/** 分期计划视图（列表 + 详情共用；详情多带 `sources` / `periods`） */
export interface InstallmentPlanView {
  id: string;
  accountId: string;
  /** 卡账户名（服务端 join 下发，端侧不查） */
  accountName: string;
  /** 展示名；🔴 缺省展示文案「信用卡分期」由**端侧**补（分类中文名不落 server/shared 字面量 —— 铁律 103） */
  name: string | null;
  srcKind: 'txn' | 'bill_amount';
  /** 本金总额（分） */
  principalTotal: number;
  periods: number;
  /** 手续费总额（分）＝ Σ 各期手续费（手改后按新值） */
  feeTotal: number;
  /** 首期账单日 `YYYY-MM-DD` */
  startBillDay: string;
  status: 'active' | 'settled' | 'cancelled';
  paidPeriods: number;
  remainingPeriods: number;
  /** 🔴 未还本金 = Σ pending.principal（**不含手续费**，`DR-060` D3） */
  remainingPrincipal: number;
  /** 下一期到期日 / 本金 / 手续费 —— 已结清 ⇒ 三键**不出**（可选键剔键） */
  nextDueDate?: string;
  nextDuePrincipal?: number;
  nextDueFee?: number;
  /** 预计结清账单日（最晚期次到期日）；无期次 ⇒ `null` */
  estimatedEndBill: string | null;
  /** 溯源：来源消费（`AC-B2.23`）—— `bill_amount` 形态为空数组（显式，不是漏带） */
  sources: InstallmentSourceView[];
  /** 🔴 未归属金额 = 本金 − Σ sources.amount（`AC-B2.23`） */
  unassignedAmount: number;
  /** 期次明细（详情用；列表也可带 —— 期次 ≤ 60 行，量级无害） */
  schedule: InstallmentPeriodView[];
}

/** `GET /api/installments` */
export interface InstallmentListResult {
  items: InstallmentPlanView[];
  /** 🔴 全部 active 计划的未还本金合计（供资产页直接渲染，端侧零加总） */
  totalRemainingPrincipal: number;
}

/** `POST /api/installments` 的返回：计划 + 生成的全部期次 */
export interface InstallmentCreatedResult {
  plan: InstallmentPlanView;
}

/** `POST /api/installments/:id/pay` 的返回 */
export interface InstallmentPaidResult {
  planId: string;
  periodIndex: number;
  /** 还款后该期状态（恒 `'paid'`） */
  status: 'paid';
  /** 🔴 本动作落的两条流水 id：[本金转账, 手续费支出]；免息分期第二项**不出**（`AC-B2.18`） */
  transactionIds: string[];
  /** 还款后计划是否全部结清 */
  settled: boolean;
}

/** `DELETE /api/installments/:id` 的返回 */
export interface InstallmentDeletedResult {
  id: string;
  deleted: true;
}
